import React from 'react';
import I18n from 'i18n-js';
import backIcon from 'images/icons/arrow-back.svg';
import PropTypes from 'prop-types';
import * as Routes from 'modules/routes';
import Cookie from 'react-cookies';
import useSlideShowStore from '@/components/admin/SlideShow/stores/useSlideShowStore';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import useFlags from '@/hooks/useFlags';
import styles from '../Navigator.module.scss';

export type BackToButtonProps = {
  disabled: boolean;
  parentId: string;
  parentType: string;
  position: string | number;
  programId: string | number;
  slideShowId: string | number;
};

const BackToButton = ({ disabled, parentId, parentType, programId, slideShowId }: BackToButtonProps) => {
  const userType = useSlideShowContext(state => state.userType);
  const slideShow = useSlideShowStore(state => state.slideShow) ?? useSlideShowContext(state => state.slideShow);
  const referrerProgramId = useSlideShowContext(state => state.referrerProgramId);
  const currentSlide = useSlideShowContext(state => state.currentSlide);
  const formattedSlides = useSlideShowContext(state => state.slides);

  const urlParams = new URLSearchParams(window.location.search);
  const { newUnitOverviewPage } = useFlags();

  // Returns empty div to preserve the 3-column grid layout in the navigator
  if (disabled) return <div />;

  const backToText = () => {
    const previousLocation = urlParams.get('from')?.toLowerCase();
    if (slideShow.activityType === 'professional_development') return I18n.t('back_to_professional_development');
    if (previousLocation === 'builder') return I18n.t('back_to_editor');
    if (previousLocation === 'unit') return I18n.t('back_to_unit');
    if (previousLocation === 'lesson') return I18n.t('back_to_lesson');
    if (urlParams.has('assignment_id') || userType === 'Student') return I18n.t('back_to_assignments');

    return I18n.t('back_to_lesson');
  };

  const getBackToPath = () => {
    if (!currentSlide) return '';
    const previousLocation = urlParams.get('from')?.toLowerCase();

    if (slideShow.activityType === 'professional_development' && userType === 'Teacher') {
      return Routes.teacher_program_professional_developments_path(referrerProgramId || programId);
    }
    if (previousLocation === 'builder') {
      const path = userType === 'Sysadmin' ? Routes.builder_admin_slide_show_path : Routes.builder_shared_slide_show_path;

      const slideToReturnTo = formattedSlides.find(slide => (slide.id === currentSlide.id)) || currentSlide;

      return path(slideShowId, {
        parent_id: parentId, parent_type: parentType, position: slideToReturnTo.position, program_id: programId,
      });
    }
    if (previousLocation === 'unit') {
      return newUnitOverviewPage
        ? Routes.shared_program_unit_path(programId, parentId)
        : Routes.stream_shared_program_investigations_path(programId, { unit_id: parentId, unit_projects: true });
    }
    if (previousLocation === 'lesson') {
      return Routes.shared_program_lesson_slide_shows_path(programId, parentId);
    }
    if (urlParams.has('assignment_id') || userType === 'Student') {
      return Routes.shared_program_assignments_path(programId, { classroom_id: Cookie.load('classroomId') });
    }

    return '';
  };

  return (
    <a
      className={`${styles.backButton} tw-flex`}
      href={getBackToPath()}
    >
      <img alt="" src={backIcon} />
      {/* AIDEV-NOTE: the img is alt="" so this span IS the link's accessible name — the reflow
          block hides it with the clip technique, never `display: none`. */}
      <span className={`${styles.backLabel} tw-ml-2 tw-text-white tw-text-lg`}>{backToText()}</span>
    </a>
  );
};

export const BackToButtonPropsTypes = PropTypes.shape({
  disabled: PropTypes.bool,
  parentId: PropTypes.string.isRequired,
  parentType: PropTypes.string.isRequired,
  position: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  slideShowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
});

BackToButton.propTypes = BackToButtonPropsTypes.isRequired;

export default BackToButton;
